import { useCallback, useRef, useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../constants';
import { toast } from 'react-toastify';

// Create axios instance with interceptors
const axiosInstance = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Request cache to prevent duplicate requests
const requestCache = new Map();
const requestTimeouts = new Map();
// Shared pending requests map so deduplication works across components
const pendingRequestsGlobal = new Map();
const CACHE_DURATION = 5000; // 5 seconds

// Request interceptor to add auth token
axiosInstance.interceptors.request.use(config => {
    // Try multiple token locations
    const token = localStorage.getItem('userAuthToken') || localStorage.getItem('authToken');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Response interceptor for error handling
axiosInstance.interceptors.response.use(
    response => response,
    error => {
        if (error.response?.status === 401) {
            // Clear tokens on auth failure
            localStorage.removeItem('userAuthToken');
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
        }
        return Promise.reject(error);
    }
);

/**
 * Hook for making API requests with caching and deduplication
 */
export const useAxios = () => {

    const getCacheKey = useCallback((method, url, data = null) => {
        return `${method}:${url}:${data ? JSON.stringify(data) : ''}`;
    }, []);

    const clearCache = useCallback((cacheKey) => {
        requestCache.delete(cacheKey);
        if (requestTimeouts.has(cacheKey)) {
            clearTimeout(requestTimeouts.get(cacheKey));
            requestTimeouts.delete(cacheKey);
        }
    }, []);

    const request = useCallback(async (method, url, data = null, options = {}) => {
        const { 
            skipCache = false, 
            cacheDuration = CACHE_DURATION,
            showError = true 
        } = options;

        const cacheKey = getCacheKey(method, url, data);

        // Check cache first
        if (!skipCache && requestCache.has(cacheKey)) {
            return requestCache.get(cacheKey);
        }

        // Check if request already pending (deduplication)
        if (pendingRequestsGlobal.has(cacheKey)) {
            return pendingRequestsGlobal.get(cacheKey);
        }

        // Create new request
        const config = method === 'GET' ? {} : {};
        
        const requestPromise = axiosInstance({
            method,
            url,
            data: method !== 'GET' ? data : undefined,
            params: method === 'GET' ? data : undefined,
            ...config
        })
            .then(response => {
                // Cache successful response
                if (!skipCache) {
                    requestCache.set(cacheKey, response.data);
                    
                    // Set timeout to clear cache
                    const timeout = setTimeout(() => {
                        clearCache(cacheKey);
                    }, cacheDuration);
                    
                    requestTimeouts.set(cacheKey, timeout);
                }
                
                pendingRequestsGlobal.delete(cacheKey);
                return response.data;
            })
            .catch(error => {
                pendingRequestsGlobal.delete(cacheKey);
                
                if (showError) {
                    const errorMsg = error.response?.data?.message || 
                                    error.response?.data?.error || 
                                    error.message;
                    toast.error(errorMsg || 'API request failed');
                }
                
                throw error;
            });

        // Store pending request globally so other components reuse it
        pendingRequestsGlobal.set(cacheKey, requestPromise);

        return requestPromise;
    }, [getCacheKey, clearCache]);

    return {
        get: useCallback((url, data = null, options = {}) => request('GET', url, data, options), [request]),
        post: useCallback((url, data = null, options = {}) => request('POST', url, data, options), [request]),
        patch: useCallback((url, data = null, options = {}) => request('PATCH', url, data, options), [request]),
        put: useCallback((url, data = null, options = {}) => request('PUT', url, data, options), [request]),
        delete: useCallback((url, data = null, options = {}) => request('DELETE', url, data, options), [request]),
        clearCache: useCallback((cacheKey) => {
            if (cacheKey) {
                clearCache(cacheKey);
            } else {
                requestCache.clear();
                requestTimeouts.forEach(timeout => clearTimeout(timeout));
                requestTimeouts.clear();
            }
        }, [clearCache])
    };
};

/**
 * Hook for loading cart data with automatic caching
 */
export const useCartApi = () => {
    const { get, post, patch, clearCache } = useAxios();

    const loadCart = useCallback(async (userId, userType = 'guest') => {
        return get(`/cart?userId=${userId}&userType=${userType}`, null, {
            cacheDuration: 10000 // Cache cart for 10 seconds
        });
    }, [get]);

    const mergeCart = useCallback(async (deviceId) => {
        // Skip cache for merge to ensure fresh data
        return post('/cart/merge', { deviceId }, { skipCache: true });
    }, [post]);

    const updateItemQuantity = useCallback(async (itemId, userId, quantity) => {
        // Clear cart cache when updating
        clearCache();
        return patch(`/cart/items/${itemId}`, { userId, quantity }, { 
            skipCache: true 
        });
    }, [patch, clearCache]);

    const removeItem = useCallback(async (itemId, userId) => {
        // Clear cart cache when removing
        clearCache();
        return axiosInstance.delete(`/cart/items/${itemId}?userId=${userId}`);
    }, [clearCache]);

    const addToCart = useCallback(async (userId, productId, variationId = null, quantity = 1) => {
        clearCache();
        const payload = {
            userId,
            userType: userId ? 'customer' : 'guest',
            productId,
            quantity
        };
        if (variationId) payload.variationId = variationId;
        return post('/cart/items', payload, { skipCache: true });
    }, [post, clearCache]);

    const clearCart = useCallback(async (userId) => {
        clearCache();
        return post('/cart/clear', { userId }, { skipCache: true });
    }, [post, clearCache]);

    const applyCoupon = useCallback(async (cartId, couponCode, couponData = {}) => {
        clearCache();
        return post('/cart/coupon', { userId: cartId, couponCode, ...couponData }, { skipCache: true });
    }, [post, clearCache]);

    const updateShipping = useCallback(async (cartId, pincode, paymentMode) => {
        return post('/cart/shipping', { 
            userId: cartId, 
            pincode, 
            paymentMode 
        }, { skipCache: true });
    }, [post]);

    return {
        loadCart,
        mergeCart,
        updateItemQuantity,
        removeItem,
        addToCart,
        clearCart,
        applyCoupon,
        updateShipping,
        clearCache
    };
    const login = useCallback(async (email, password) => {
        return post('/login', { email, password }, { skipCache: true });
    }, [post]);

    const register = useCallback(async (userData) => {
        return post('/register', userData, { skipCache: true });
    }, [post]);

    const getUser = useCallback(async () => {
        return get('/user');
    }, [get]);

    const logout = useCallback(() => {
        clearCache(); // Clear all caches on logout
        localStorage.removeItem('userAuthToken');
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
    }, [clearCache]);

    return {
        login,
        register,
        getUser,
        logout
    };
};

/**
 * Hook for product APIs
 */
export const useProductApi = () => {
    const { get } = useAxios();

    const getProducts = useCallback(async (filters = {}) => {
        const queryString = new URLSearchParams(filters).toString();
        return get(`/products?${queryString}`);
    }, [get]);

    const getProduct = useCallback(async (slug) => {
        return get(`/products/${slug}`);
    }, [get]);

    return {
        getProducts,
        getProduct
    };
};

export default axiosInstance;
